import { Fragment, type RefObject } from 'react';
import { Box, Flex, Input, Text, type TextProps } from '@chakra-ui/react';
import { splitForEdit, joinForEdit, tokenizeTags } from './taggedRuns';

// `unicode-bidi: isolate` gives each run its own bidi context, so the ASCII
// `<t0>` tokens stop mirroring/reordering against RTL text — the runs lay out in
// logical order per the container's direction.
const ISOLATE = { unicodeBidi: 'isolate' } as const;

// A formatting tag rendered as an inert, direction-safe token rather than raw
// `<t0>` text that bidi would scramble.
export function TagChip({ raw }: { raw: string }) {
  return (
    <Box
      as="span"
      dir="ltr"
      sx={ISOLATE}
      fontFamily="mono"
      fontSize="xs"
      color="fg.muted"
      bg="bg.muted"
      borderRadius="sm"
      px={1}
      userSelect="none"
      whiteSpace="nowrap"
    >
      {raw}
    </Box>
  );
}

// Read-only rendering of a (possibly tagged) segment value: text runs in the
// segment's direction, tags as isolated chips. Fixes the RTL display without
// touching the underlying string.
export function TaggedText({
  value,
  dir,
  ...textProps
}: { value: string; dir: 'ltr' | 'rtl' } & TextProps) {
  const runs = tokenizeTags(value);
  let offset = 0;
  return (
    <Text dir={dir} {...textProps}>
      {runs.map((run) => {
        const key = offset;
        offset += run.kind === 'tag' ? run.raw.length : run.text.length;
        return run.kind === 'tag' ? (
          <TagChip key={key} raw={run.raw} />
        ) : (
          <Box as="span" key={key} sx={ISOLATE}>
            {run.text}
          </Box>
        );
      })}
    </Text>
  );
}

// Editable segment field: each text slot is an input, each tag a fixed chip the
// author can't edit or delete — so the tag set is always preserved on save, and
// the RTL layout reads correctly. When the override is empty it seeds from the
// machine value so the tag scaffold is present to edit; clearing every slot emits
// '' so the existing empty-means-revert behaviour is unchanged.
export function TaggedInput({
  value,
  seedValue,
  dir,
  onChange,
  placeholder,
  inputRef,
  'data-testid': dataTestId,
}: {
  value: string;
  seedValue: string;
  dir: 'ltr' | 'rtl';
  onChange: (value: string) => void;
  placeholder?: string;
  inputRef?: RefObject<HTMLInputElement | null>;
  'data-testid'?: string;
}) {
  // An empty value always means the author emptied the field, which is how an
  // override is reverted. It cannot mean "not seeded yet": the workbench derives
  // draftValue from the same currentMachineValue this seed comes from, so an
  // untouched row already holds that text, and a row without one seeds ''.
  // Falling back to the seed for the text slots therefore only ever refilled a
  // field the author had just cleared -- on the next render, so it could never
  // be emptied at all.
  //
  // The seed is still what the tag scaffold is read from, so the chips stay on
  // screen while the field is empty and the author can type into them again.
  const scaffold = splitForEdit(value.trim() === '' ? seedValue : value);
  const tags = scaffold.tags;
  const texts = value.trim() === '' ? scaffold.texts.map(() => '') : scaffold.texts;
  const mainIndex = texts.reduce(
    (best, text, i) => (text.length > texts[best]!.length ? i : best),
    0,
  );

  const setSlot = (index: number, next: string) => {
    const nextTexts = texts.map((text, i) => (i === index ? next : text));
    const allEmpty = nextTexts.every((text) => text.trim() === '');
    onChange(allEmpty ? '' : joinForEdit(nextTexts, tags));
  };

  // Precompute per-slot render descriptors with a stable key from each slot's
  // character offset (not the array index).
  let acc = 0;
  const slots = texts.map((text, index) => {
    const key = acc;
    const tag = index < tags.length ? tags[index]! : null;
    acc += text.length + (tag?.length ?? 0);
    return { key, index, text, tag, isMain: index === mainIndex };
  });

  return (
    <Flex
      dir={dir}
      wrap="wrap"
      align="center"
      gap={1}
      borderWidth="1px"
      borderColor="border.emphasized"
      borderRadius="md"
      px={2}
      py={1}
      data-testid={dataTestId}
    >
      {slots.map((slot) => (
        <Fragment key={slot.key}>
          <Input
            ref={slot.isMain ? inputRef : undefined}
            value={slot.text}
            onChange={(event) => setSlot(slot.index, event.target.value)}
            dir={dir}
            sx={ISOLATE}
            variant="unstyled"
            size="sm"
            flex={slot.text.length === 0 ? '0 1 auto' : '1 1 auto'}
            minW={slot.text.length === 0 ? 6 : 16}
            textAlign={dir === 'rtl' ? 'right' : 'left'}
            placeholder={slot.isMain ? placeholder : undefined}
          />
          {slot.tag !== null ? <TagChip raw={slot.tag} /> : null}
        </Fragment>
      ))}
    </Flex>
  );
}
